function firstMeaningfulText(node: Node): Text | null {
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT)
  let last: Text | null = null
  while (walker.nextNode()) {
    const text = walker.currentNode as Text
    if (text.data.replace(/\u200B/g, '').trim()) last = text
  }
  return last
}

export const SOFT_LINE_BREAK_PLACEHOLDER_ATTRIBUTE = 'data-editor-soft-break-placeholder'

function emptyListItemEntry(): HTMLBRElement {
  const entry = document.createElement('br')
  entry.dataset.checklistEmpty = 'true'
  return entry
}

/**
 * Keeps a real editable boundary before a checklist's trailing non-editable
 * control. A `<br>` is Chromium's native empty-line representation; retaining a
 * zero-width text sentinel causes keyboard caret navigation to stall on some
 * Android and desktop Chromium paths.
 */
export function ensureChecklistItemTextEntry(item: HTMLLIElement): Text | null {
  const checkbox = item.querySelector<HTMLInputElement>(':scope > input[type="checkbox"]')
  if (!checkbox) return null
  const editableChildren = () => [...item.childNodes].slice(0, [...item.childNodes].indexOf(checkbox))
  const authoredText = () => firstMeaningfulText(item)
  const existingText = authoredText()
  if (existingText) {
    for (const child of editableChildren()) {
      if (child instanceof HTMLBRElement && child.dataset.checklistEmpty === 'true') child.remove()
    }
    const directTextChildren = editableChildren().filter((child): child is Text => child instanceof Text)
    const needsCleanup = directTextChildren.length > 1
      || directTextChildren.some((child) => child.data.includes('\u200B'))
    if (!needsCleanup) return existingText
    for (const child of directTextChildren) {
      child.data = child.data.replace(/\u200B/g, '')
    }
    item.normalize()
    return authoredText()
  }
  for (const child of editableChildren()) {
    if (child instanceof Text) child.remove()
  }
  const existingEntry = editableChildren().find((child): child is HTMLBRElement => child instanceof HTMLBRElement)
  if (existingEntry) {
    existingEntry.dataset.checklistEmpty = 'true'
  } else {
    item.insertBefore(emptyListItemEntry(), checkbox)
  }
  return null
}

export function ensureChecklistTextEntries(root: HTMLElement): void {
  for (const item of root.querySelectorAll<HTMLLIElement>('ul[data-checklist] > li')) {
    ensureChecklistItemTextEntry(item)
  }
}

export type ListType = 'bullet' | 'checklist' | 'ordered'

export const EDITOR_TEXT_INDENT = '\u00a0\u00a0'

export interface EditorFormatState {
  bold: boolean
  bulletList: boolean
  checklist: boolean
  codeBlock: boolean
  heading: boolean
  headingLevel: 'regular' | 'h1' | 'h2'
  highlight: boolean
  italic: boolean
  link: boolean
  orderedList: boolean
  quote: boolean
  underline: boolean
}

export const EMPTY_EDITOR_FORMAT_STATE: EditorFormatState = Object.freeze({
  bold: false,
  bulletList: false,
  checklist: false,
  codeBlock: false,
  heading: false,
  headingLevel: 'regular',
  highlight: false,
  italic: false,
  link: false,
  orderedList: false,
  quote: false,
  underline: false,
})

interface CaretPosition {
  node: Node
  offset: number
}

function firstLogicalLine(root: HTMLElement): Node {
  let line: Node = [...root.childNodes].find((node) => {
    if (node instanceof Text && !node.data.trim()) return false
    return !(node instanceof HTMLElement && node.dataset.linkPreviewSource === 'true')
  }) ?? root
  while (line instanceof HTMLElement) {
    if (line.matches('ul, ol')) {
      line = line.querySelector(':scope > li') ?? line
      continue
    }
    if (line.matches('blockquote') || (line.matches('li') && line.firstElementChild?.matches('p, div'))) {
      line = line.firstChild ?? line
      continue
    }
    break
  }
  return line
}

function endOfLogicalLine(line: Node): CaretPosition {
  if (line instanceof Text) {
    const newline = line.data.indexOf('\n')
    return { node: line, offset: newline < 0 ? line.length : newline }
  }
  if (!(line instanceof HTMLElement)) return { node: line, offset: 0 }
  if (line.matches('hr')) {
    const parent = line.parentNode
    return parent
      ? { node: parent, offset: [...parent.childNodes].indexOf(line) + 1 }
      : { node: line, offset: 0 }
  }

  let latest: CaretPosition | null = null
  const visit = (node: Node): boolean => {
    if (node instanceof Text) {
      const newline = node.data.indexOf('\n')
      latest = { node, offset: newline < 0 ? node.length : newline }
      return newline >= 0
    }
    if (!(node instanceof HTMLElement)) return false
    for (const [index, child] of [...node.childNodes].entries()) {
      if (
        child instanceof HTMLElement
        && (child.matches('br, input, ul, ol') || child.contentEditable === 'false')
      ) {
        if (!latest) latest = { node, offset: index }
        return true
      }
      if (visit(child)) return true
    }
    return false
  }
  visit(line)
  return latest ?? { node: line, offset: 0 }
}

function endOfVisibleEditorContent(root: HTMLElement): CaretPosition {
  let latest: CaretPosition = { node: root, offset: 0 }

  const visit = (node: Node): void => {
    if (node instanceof Text) {
      // Marked preserves line-feed text nodes between root blocks. They are DOM
      // formatting, not an editor line, and Chromium renders a caret there at
      // the trailing list control's boundary.
      if (
        !node.data.trim()
        && (node.parentNode === root || node.parentElement?.matches('ul, ol'))
      ) return
      latest = { node, offset: node.length }
      return
    }
    if (!(node instanceof HTMLElement)) return
    if (
      node.dataset.linkPreviewSource === 'true'
      || node.matches('input')
      || node.contentEditable === 'false'
    ) return

    for (const [index, child] of [...node.childNodes].entries()) {
      if (child instanceof HTMLBRElement) {
        latest = { node, offset: index }
      } else {
        visit(child)
      }
    }
  }

  visit(root)
  return latest
}

export function placeCaretAtEndOfFirstLine(root: HTMLElement): Range | null {
  const selection = window.getSelection()
  if (!selection) return null
  ensureChecklistTextEntries(root)
  const position = endOfLogicalLine(firstLogicalLine(root))
  const range = document.createRange()
  range.setStart(position.node, position.offset)
  range.collapse(true)
  selection.removeAllRanges()
  selection.addRange(range)
  return range
}

export function placeCaretAtEnd(root: HTMLElement): Range | null {
  const selection = window.getSelection()
  if (!selection) return null
  ensureChecklistTextEntries(root)
  const position = endOfVisibleEditorContent(root)
  const range = document.createRange()
  range.setStart(position.node, position.offset)
  range.collapse(true)
  selection.removeAllRanges()
  selection.addRange(range)
  return range
}

function checklistItemBeforeBoundary(container: Node, offset: number): HTMLLIElement | null {
  const previous = container.childNodes[offset - 1]
  if (previous instanceof HTMLLIElement) return previous
  if (!(previous instanceof HTMLElement) || previous.tagName !== 'UL') return null
  const item = previous.lastElementChild
  return item instanceof HTMLLIElement ? item : null
}

export function normalizeChecklistCaretBoundary(
  root: HTMLElement,
  sourceRange: Range,
): Range | null {
  if (!sourceRange.collapsed) return null
  const directItem = sourceRange.startContainer instanceof HTMLLIElement
    ? sourceRange.startContainer
    : null
  const boundaryItem = checklistItemBeforeBoundary(
    sourceRange.startContainer,
    sourceRange.startOffset,
  )
  const item = directItem
    ?? (sourceRange.startContainer instanceof HTMLElement
      ? sourceRange.startContainer.closest('li')
      : sourceRange.startContainer.parentElement?.closest('li'))
    ?? boundaryItem
  const list = item?.parentElement
  if (!item || !root.contains(item) || list?.tagName !== 'UL' || !list.hasAttribute('data-checklist')) return null

  const checkbox = item.querySelector<HTMLInputElement>(':scope > input[type="checkbox"]')
  if (!checkbox) return null
  const checkboxIndex = [...item.childNodes].indexOf(checkbox)
  const isItemCheckboxBoundary = directItem !== null && sourceRange.startOffset >= checkboxIndex
  const isStructuralCheckboxBoundary = boundaryItem === item
  const isCheckboxDescendant = sourceRange.startContainer === checkbox
    || checkbox.contains(sourceRange.startContainer)
  if (!isItemCheckboxBoundary && !isStructuralCheckboxBoundary && !isCheckboxDescendant) return null

  ensureChecklistItemTextEntry(item)
  const position = endOfLogicalLine(item)
  const normalized = document.createRange()
  normalized.setStart(position.node, position.offset)
  normalized.collapse(true)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(normalized)
  return normalized
}

export function editorFormatStatesEqual(
  left: EditorFormatState,
  right: EditorFormatState,
): boolean {
  return (
    left.bold === right.bold &&
    left.bulletList === right.bulletList &&
    left.checklist === right.checklist &&
    left.codeBlock === right.codeBlock &&
    left.heading === right.heading &&
    left.headingLevel === right.headingLevel &&
    left.highlight === right.highlight &&
    left.italic === right.italic &&
    left.link === right.link &&
    left.orderedList === right.orderedList &&
    left.quote === right.quote &&
    left.underline === right.underline
  )
}

function elementWithin(root: HTMLElement, node: Node | null): HTMLElement | null {
  const element = node instanceof HTMLElement ? node : node?.parentElement
  return element && root.contains(element) ? element : null
}

function hasInlineFormat(root: HTMLElement, range: Range, selector: string): boolean {
  if (range.collapsed) return Boolean(elementWithin(root, range.startContainer)?.closest(selector))
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const selected: Text[] = []
  while (walker.nextNode()) {
    const text = walker.currentNode as Text
    if (!text.data.trim() || !range.intersectsNode(text)) continue
    selected.push(text)
  }
  return selected.length > 0 && selected.every((text) => Boolean(text.parentElement?.closest(selector)))
}

const WORD_CHARACTER = /[\p{L}\p{N}\p{M}_'’\-]/u
const WORD_CORE_CHARACTER = /[\p{L}\p{N}\p{M}_]/u

function isWordCharacter(character: string | undefined): boolean {
  return Boolean(character && WORD_CHARACTER.test(character))
}

function edgeTextNode(node: Node | undefined, edge: 'first' | 'last'): Text | null {
  if (!node) return null
  if (node instanceof Text) return node
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT)
  if (edge === 'first') return walker.nextNode() as Text | null
  let result: Text | null = null
  while (walker.nextNode()) result = walker.currentNode as Text
  return result
}

function resolveTextCaret(sourceRange: Range): { node: Text; offset: number } | null {
  if (sourceRange.startContainer instanceof Text) {
    return { node: sourceRange.startContainer, offset: sourceRange.startOffset }
  }
  const container = sourceRange.startContainer
  const next = edgeTextNode(container.childNodes[sourceRange.startOffset], 'first')
  if (next && isWordCharacter(next.data[0])) return { node: next, offset: 0 }
  const previous = edgeTextNode(container.childNodes[sourceRange.startOffset - 1], 'last')
  if (previous && isWordCharacter(previous.data.at(-1))) {
    return { node: previous, offset: previous.length }
  }
  return null
}

export function formatWordAtCaret(
  sourceRange: Range,
  tagName: 'strong' | 'em' | 'mark' | 'u',
): Range | null {
  if (!sourceRange.collapsed) return null
  const position = resolveTextCaret(sourceRange)
  if (!position) return null
  const textNode = position.node
  const text = textNode.data
  const offset = position.offset
  const pivot = isWordCharacter(text[offset])
    ? offset
    : isWordCharacter(text[offset - 1])
      ? offset - 1
      : -1
  if (pivot < 0) return null

  let start = pivot
  let end = pivot + 1
  while (start > 0 && isWordCharacter(text[start - 1])) start -= 1
  while (end < text.length && isWordCharacter(text[end])) end += 1
  while (start < end && !WORD_CORE_CHARACTER.test(text[start]!)) start += 1
  while (end > start && !WORD_CORE_CHARACTER.test(text[end - 1]!)) end -= 1
  if (start === end || offset < start || offset > end) return null

  const wordRange = document.createRange()
  wordRange.setStart(textNode, start)
  wordRange.setEnd(textNode, end)
  const wrapper = document.createElement(tagName)
  wrapper.append(wordRange.extractContents())
  wordRange.insertNode(wrapper)

  const caret = document.createRange()
  const formattedText = wrapper.firstChild
  if (!(formattedText instanceof Text)) return null
  caret.setStart(formattedText, Math.min(offset - start, formattedText.length))
  caret.collapse(true)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(caret)
  return caret
}

export function getEditorFormatState(
  root: HTMLElement,
  range: Range,
  focusNode: Node | null = range.startContainer,
): EditorFormatState {
  const focus = elementWithin(root, focusNode) ?? elementWithin(root, range.startContainer)
  const item = focus?.closest('li')
  const listType = item?.parentElement ? getListType(item.parentElement) : null
  const heading = focus?.closest('h1, h2, h3, h4, h5, h6')
  return {
    bold: hasInlineFormat(root, range, 'strong, b'),
    bulletList: listType === 'bullet',
    checklist: listType === 'checklist',
    codeBlock: Boolean(focus?.closest('pre')),
    heading: Boolean(heading),
    headingLevel: heading?.tagName === 'H1' ? 'h1' : heading?.tagName === 'H2' ? 'h2' : 'regular',
    highlight: hasInlineFormat(root, range, 'mark'),
    italic: hasInlineFormat(root, range, 'em, i'),
    link: hasInlineFormat(root, range, 'a'),
    orderedList: listType === 'ordered',
    quote: Boolean(focus?.closest('blockquote')),
    underline: hasInlineFormat(root, range, 'u'),
  }
}

/** Returns the next block in the editor's Regular -> H1 -> H2 heading cycle. */
export function nextHeadingTag(currentTagName: string | null | undefined): 'p' | 'h1' | 'h2' {
  const tagName = currentTagName?.toUpperCase()
  if (tagName === 'H1') return 'h2'
  if (tagName && /^H[2-6]$/.test(tagName)) return 'p'
  return 'h1'
}

export function unwrapInlineScopeAtCaret(element: HTMLElement, sourceRange: Range): Range {
  const marker = document.createElement('span')
  marker.setAttribute('data-editor-caret-marker', '')
  sourceRange.cloneRange().insertNode(marker)
  const parent = element.parentNode
  if (!parent) return sourceRange
  while (element.firstChild) parent.insertBefore(element.firstChild, element)
  element.remove()
  const markerParent = marker.parentNode
  if (!markerParent) return sourceRange
  const range = document.createRange()
  const left: Text[] = []
  const right: Text[] = []
  for (let node = marker.previousSibling; node instanceof Text; node = node.previousSibling) {
    left.unshift(node)
  }
  for (let node = marker.nextSibling; node instanceof Text; node = node.nextSibling) {
    right.push(node)
  }
  if (left.length || right.length) {
    const caretOffset = left.reduce((length, node) => length + node.length, 0)
    const merged = document.createTextNode([...left, ...right].map((node) => node.data).join(''))
    markerParent.insertBefore(merged, left[0] ?? marker)
    for (const node of [...left, ...right]) node.remove()
    marker.remove()
    range.setStart(merged, caretOffset)
  } else {
    const markerIndex = [...markerParent.childNodes].indexOf(marker)
    marker.remove()
    range.setStart(markerParent, markerIndex)
  }
  range.collapse(true)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
  return range
}

export function insertCodeLineBreakAtRange(codeBlock: HTMLElement, sourceRange: Range): Range {
  const range = sourceRange.cloneRange()
  range.deleteContents()
  const textNode = document.createTextNode('\n')
  range.insertNode(textNode)
  const tail = document.createRange()
  tail.setStartAfter(textNode)
  tail.setEnd(codeBlock, codeBlock.childNodes.length)
  if (tail.toString()) {
    range.setStart(textNode, textNode.length)
  } else {
    const placeholder = document.createElement('br')
    textNode.after(placeholder)
    range.setStartBefore(placeholder)
  }
  range.collapse(true)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
  return range
}

function hasEditableContentAfter(node: Node, scope: HTMLElement): boolean {
  const hasEditableContent = (candidate: Node): boolean => {
    if (candidate instanceof Text) return candidate.data.length > 0
    if (!(candidate instanceof HTMLElement)) return false
    if (candidate.matches('input, [contenteditable="false"]')) return false
    if (candidate.matches('br, img')) return true
    return [...candidate.childNodes].some(hasEditableContent)
  }

  let current: Node | null = node
  while (current && current !== scope) {
    for (let sibling = current.nextSibling; sibling; sibling = sibling.nextSibling) {
      // A following block (including the editor's disposable final paragraph)
      // cannot host the caret for a soft break in the current inline run.
      if (sibling instanceof HTMLElement && sibling.matches('p, div, ul, ol, blockquote, pre, h1, h2, h3, h4, h5, h6, hr')) return false
      if (hasEditableContent(sibling)) return true
    }
    current = current.parentNode
  }
  return false
}

/** Inserts one semantic soft break while retaining the current Markdown block. */
export function insertSoftLineBreakAtRange(scope: HTMLElement, sourceRange: Range): Range {
  const range = sourceRange.cloneRange()
  range.deleteContents()
  const lineBreak = document.createElement('br')
  range.insertNode(lineBreak)

  if (hasEditableContentAfter(lineBreak, scope)) {
    range.setStartAfter(lineBreak)
  } else {
    const placeholder = document.createElement('br')
    placeholder.setAttribute(SOFT_LINE_BREAK_PLACEHOLDER_ATTRIBUTE, 'true')
    lineBreak.after(placeholder)
    range.setStartBefore(placeholder)
  }
  range.collapse(true)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
  return range
}

const CODE_LINE_BLOCK_TAGS = new Set(['DIV', 'P'])

function codeBlockPlainText(element: HTMLElement): string {
  let text = ''
  const children = [...element.childNodes]
  for (const [index, child] of children.entries()) {
    if (child instanceof Text) {
      text += child.data
      continue
    }
    if (!(child instanceof HTMLElement)) continue
    if (child.tagName === 'BR') {
      text += '\n'
      continue
    }
    const isLineBlock = CODE_LINE_BLOCK_TAGS.has(child.tagName)
    if (isLineBlock && text && !text.endsWith('\n')) text += '\n'
    text += codeBlockPlainText(child)
    if (isLineBlock && index < children.length - 1 && !text.endsWith('\n')) text += '\n'
  }
  return text
}

function normalTextBlock(text: string): HTMLParagraphElement {
  const paragraph = document.createElement('p')
  if (!text) {
    paragraph.append(document.createElement('br'))
    return paragraph
  }
  for (const [index, line] of text.split('\n').entries()) {
    if (index > 0) paragraph.append(document.createElement('br'))
    if (line) paragraph.append(document.createTextNode(line))
  }
  return paragraph
}

function positionAtTextOffset(
  paragraph: HTMLParagraphElement,
  requestedOffset: number,
): CaretPosition {
  let offset = Math.max(0, requestedOffset)
  for (const [index, child] of [...paragraph.childNodes].entries()) {
    if (child instanceof Text) {
      if (offset <= child.length) return { node: child, offset }
      offset -= child.length
      continue
    }
    if (child instanceof HTMLBRElement) {
      if (offset === 0) return { node: paragraph, offset: index }
      offset -= 1
    }
  }
  return { node: paragraph, offset: paragraph.childNodes.length }
}

function uniqueCodeMarker(text: string, name: string): string {
  let index = 0
  let marker = `\uE000minimany-${name}\uE001`
  while (text.includes(marker)) {
    index += 1
    marker = `\uE000minimany-${name}-${index}\uE001`
  }
  return marker
}

export function unwrapCodeBlock(codeBlock: HTMLElement, sourceRange: Range): Range {
  const wasCollapsed = sourceRange.collapsed
  const originalText = codeBlockPlainText(codeBlock)
  const startMarker = uniqueCodeMarker(originalText, 'code-start')
  const endMarker = uniqueCodeMarker(`${originalText}${startMarker}`, 'code-end')

  if (!wasCollapsed) {
    const end = sourceRange.cloneRange()
    end.collapse(false)
    end.insertNode(document.createTextNode(endMarker))
  }
  const start = sourceRange.cloneRange()
  start.collapse(true)
  start.insertNode(document.createTextNode(startMarker))

  const markedText = codeBlockPlainText(codeBlock)
  const startOffset = markedText.indexOf(startMarker)
  const markedEndOffset = wasCollapsed
    ? startOffset
    : markedText.indexOf(endMarker)
  const endOffset = wasCollapsed
    ? startOffset
    : markedEndOffset - startMarker.length
  const text = markedText.replace(startMarker, '').replace(endMarker, '')
  const paragraph = normalTextBlock(text)
  codeBlock.replaceWith(paragraph)

  const range = document.createRange()
  const startPosition = positionAtTextOffset(paragraph, startOffset)
  const endPosition = positionAtTextOffset(paragraph, endOffset)
  range.setStart(startPosition.node, startPosition.offset)
  range.setEnd(endPosition.node, endPosition.offset)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
  return range
}

export function ensureTrailingCodeBlockExit(root: HTMLElement): HTMLParagraphElement | null {
  let lastMeaningfulChild = root.lastChild
  while (
    lastMeaningfulChild instanceof Text
    && !lastMeaningfulChild.data.trim()
  ) {
    lastMeaningfulChild = lastMeaningfulChild.previousSibling
  }
  if (!(lastMeaningfulChild instanceof HTMLPreElement)) return null
  const paragraph = document.createElement('p')
  paragraph.append(document.createElement('br'))
  root.append(paragraph)
  return paragraph
}

export function getListType(list: Element): ListType | null {
  if (list.tagName === 'OL') return 'ordered'
  if (list.tagName !== 'UL') return null
  return list.hasAttribute('data-checklist') ? 'checklist' : 'bullet'
}

function isEmptyListSeparator(node: Node): boolean {
  if (node instanceof Text) return !node.data.trim()
  if (!(node instanceof HTMLElement) || !node.matches('p, div')) return false
  return [...node.childNodes].every((child) =>
    (child instanceof Text && !child.data.trim()) || child instanceof HTMLBRElement,
  )
}

function blockHasAuthoredContent(block: HTMLElement): boolean {
  return [...block.childNodes].some((child) => {
    if (child instanceof Text) return Boolean(child.data.replace(/\u200B/g, '').trim())
    if (!(child instanceof HTMLElement)) return false
    if (child instanceof HTMLBRElement) return false
    return Boolean(child.textContent?.replace(/\u200B/g, '').trim() || child.querySelector('img, hr, ul, ol, pre'))
  })
}

/**
 * Contenteditable may split one visual list into sibling list elements during
 * paste, Backspace, or Delete. Markdown treats those element boundaries as a
 * blank paragraph, so canonicalize consecutive lists back into one semantic
 * block. Explicitly exiting a list creates a paragraph after one complete list,
 * not an empty separator between two lists of the same type.
 */
export function mergeConsecutiveLists(root: HTMLElement): void {
  const parents: HTMLElement[] = [root, ...root.querySelectorAll<HTMLLIElement>('li')]
  for (const parent of parents) {
    let list: Element | null = parent.firstElementChild
    while (list) {
      const type = getListType(list)
      if (!type) {
        list = list.nextElementSibling
        continue
      }
      const removable: Node[] = []
      let candidate: Node | null = list.nextSibling
      while (candidate && isEmptyListSeparator(candidate)) {
        removable.push(candidate)
        candidate = candidate.nextSibling
      }
      const nextList = candidate instanceof HTMLElement && getListType(candidate) === type
        ? candidate
        : null
      if (nextList) {
        for (const separator of removable) separator.parentNode?.removeChild(separator)
        while (nextList.firstChild) list.append(nextList.firstChild)
        nextList.remove()
        continue
      }
      list = list.nextElementSibling
    }
  }
}

interface PastedListRow {
  checked: boolean | null
  content: DocumentFragment
}

function moveEditableItemContent(item: HTMLLIElement): DocumentFragment {
  const content = document.createDocumentFragment()
  for (const child of [...item.childNodes]) {
    if (child instanceof HTMLInputElement && child.type === 'checkbox') {
      child.remove()
      continue
    }
    if (child instanceof HTMLElement && child.matches('p, div')) {
      while (child.firstChild) content.append(child.firstChild)
      child.remove()
      continue
    }
    content.append(child)
  }
  for (const input of content.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')) {
    input.remove()
  }
  return content
}

function pastedRows(html: string): PastedListRow[] | null {
  const container = document.createElement('div')
  container.innerHTML = html
  const meaningful = [...container.childNodes].filter((node) =>
    !(node instanceof Text && !node.data.trim()),
  )
  const hasList = meaningful.some((node) => node instanceof HTMLElement && Boolean(getListType(node)))
  if (!hasList && meaningful.length < 2) return null

  const rows: PastedListRow[] = []
  for (const node of meaningful) {
    if (node instanceof HTMLElement && getListType(node)) {
      const sourceChecklist = getListType(node) === 'checklist'
      for (const item of [...node.children]) {
        if (!(item instanceof HTMLLIElement)) continue
        const checkbox = item.querySelector<HTMLInputElement>(':scope > input[type="checkbox"]')
        rows.push({
          checked: sourceChecklist ? Boolean(checkbox?.checked) : null,
          content: moveEditableItemContent(item),
        })
      }
      continue
    }
    const content = document.createDocumentFragment()
    if (node instanceof HTMLElement && node.matches('p, div')) {
      while (node.firstChild) content.append(node.firstChild)
    } else {
      content.append(node)
    }
    rows.push({ checked: null, content })
  }
  return rows.length ? rows : null
}

function itemHasAuthoredContent(item: HTMLLIElement): boolean {
  return [...item.childNodes].some((child) => {
    if (child instanceof HTMLInputElement && child.type === 'checkbox') return false
    if (child instanceof HTMLBRElement) return false
    if (child instanceof Text) return Boolean(child.data.replace(/\u200B/g, '').trim())
    return child instanceof HTMLElement && Boolean(
      child.textContent?.trim() || child.querySelector('img, hr, ul, ol, pre'),
    )
  })
}

function removeEmptyItemEntries(item: HTMLLIElement): void {
  for (const child of [...item.childNodes]) {
    if (child instanceof HTMLBRElement && !itemHasAuthoredContent(item)) child.remove()
    if (child instanceof Text && !child.data.replace(/\u200B/g, '').trim()) child.remove()
  }
}

function appendDestinationCheckbox(item: HTMLLIElement, checked: boolean): void {
  const checkbox = document.createElement('input')
  checkbox.type = 'checkbox'
  checkbox.checked = checked
  item.append(checkbox)
}

/**
 * Inserts block/list paste content into the list that owns the caret. Source
 * markers are discarded; the receiving list supplies bullet, checklist, or
 * numbering semantics. Returns null when ordinary browser insertion is more
 * appropriate (for example, a single inline paragraph).
 */
export function insertPastedEditorHtml(
  root: HTMLElement,
  sourceRange: Range,
  html: string,
): Range | null {
  const startElement = sourceRange.startContainer instanceof HTMLElement
    ? sourceRange.startContainer
    : sourceRange.startContainer.parentElement
  const item = startElement?.closest('li') as HTMLLIElement | null
  const list = item?.parentElement
  const destinationType = list ? getListType(list) : null
  if (
    !item
    || !list
    || !destinationType
    || !root.contains(item)
    || (!sourceRange.collapsed && !item.contains(sourceRange.endContainer))
  ) return null

  const rows = pastedRows(html)
  if (!rows) return null

  const range = sourceRange.cloneRange()
  range.deleteContents()
  const checkbox = destinationType === 'checklist'
    ? item.querySelector<HTMLInputElement>(':scope > input[type="checkbox"]')
    : null
  if (checkbox && checkbox !== item.lastChild) item.append(checkbox)
  const editableChildCount = checkbox
    ? [...item.childNodes].indexOf(checkbox)
    : item.childNodes.length
  if (range.startContainer === item && range.startOffset > editableChildCount) {
    range.setStart(item, editableChildCount)
    range.collapse(true)
  }

  const wasEmpty = !itemHasAuthoredContent(item)
  const tailRange = document.createRange()
  tailRange.setStart(range.startContainer, range.startOffset)
  tailRange.setEnd(item, editableChildCount)
  const tail = tailRange.extractContents()
  if (wasEmpty) {
    for (const child of [...tail.childNodes]) {
      if (
        child instanceof HTMLBRElement
        || (child instanceof Text && !child.data.replace(/\u200B/g, '').trim())
      ) child.remove()
    }
  }
  removeEmptyItemEntries(item)

  const [first, ...remaining] = rows
  item.insertBefore(first!.content, checkbox)
  if (wasEmpty && checkbox && first!.checked !== null) checkbox.checked = first!.checked

  let finalItem = item
  const destinationItems = [item]
  for (const row of remaining) {
    const nextItem = document.createElement('li')
    nextItem.append(row.content)
    if (destinationType === 'checklist') appendDestinationCheckbox(nextItem, Boolean(row.checked))
    finalItem.after(nextItem)
    finalItem = nextItem
    destinationItems.push(nextItem)
  }

  const marker = document.createElement('span')
  marker.dataset.editorPasteCaret = 'true'
  const finalCheckbox = destinationType === 'checklist'
    ? finalItem.querySelector<HTMLInputElement>(':scope > input[type="checkbox"]')
    : null
  finalItem.insertBefore(marker, finalCheckbox)
  finalItem.insertBefore(tail, finalCheckbox)

  for (const destinationItem of destinationItems) {
    if (!itemHasAuthoredContent(destinationItem) && !destinationItem.querySelector(':scope > br')) {
      destinationItem.insertBefore(
        destinationType === 'checklist' ? emptyListItemEntry() : document.createElement('br'),
        destinationItem.querySelector(':scope > input[type="checkbox"]'),
      )
    }
    if (destinationType === 'checklist') ensureChecklistItemTextEntry(destinationItem)
  }

  const markerParent = marker.parentNode
  if (!markerParent) return null
  const markerIndex = [...markerParent.childNodes].indexOf(marker)
  marker.remove()
  const caret = document.createRange()
  caret.setStart(markerParent, markerIndex)
  caret.collapse(true)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(caret)
  return caret
}

function setChecklistControls(list: HTMLElement, enabled: boolean): void {
  list.toggleAttribute('data-checklist', enabled)
  for (const item of list.querySelectorAll<HTMLLIElement>(':scope > li')) {
    const checkbox = item.querySelector<HTMLInputElement>(':scope > input[type="checkbox"]')
    if (!enabled) {
      checkbox?.remove()
      continue
    }
    if (checkbox) {
      if (checkbox !== item.lastChild) item.append(checkbox)
      continue
    }
    const input = document.createElement('input')
    input.type = 'checkbox'
    item.append(input)
  }
}

export function convertList(list: HTMLElement, target: ListType): HTMLElement {
  const targetTag = target === 'ordered' ? 'OL' : 'UL'
  let converted = list
  if (list.tagName !== targetTag) {
    converted = document.createElement(targetTag.toLowerCase())
    while (list.firstChild) converted.append(list.firstChild)
    list.replaceWith(converted)
  }
  setChecklistControls(converted, target === 'checklist')
  if (target !== 'ordered') converted.removeAttribute('start')
  return converted
}

/**
 * Converts a contiguous selection of top-level text blocks into one semantic
 * list with one item per source block. Browser list commands can instead wrap
 * several block elements in a single LI, which looks correct in contenteditable
 * but serializes as one Markdown item.
 */
export function wrapBlocksInList(
  root: HTMLElement,
  startBlock: HTMLElement,
  endBlock: HTMLElement,
  type: ListType,
): HTMLLIElement | null {
  if (startBlock.parentElement !== root || endBlock.parentElement !== root) return null

  const rootBlocks = [...root.children]
  const startIndex = rootBlocks.indexOf(startBlock)
  const endIndex = rootBlocks.indexOf(endBlock)
  if (startIndex < 0 || endIndex <= startIndex) return null

  const selectedBlocks = rootBlocks.slice(startIndex, endIndex + 1)
  if (!selectedBlocks.every((block) => block.matches('p, div'))) return null
  // A selected visual gap is not a list item. Keep item boundaries faithful to
  // authored content, so a paragraph created by an extra Enter cannot become a
  // blank bullet/task/numbered row.
  const sourceBlocks = selectedBlocks.filter(
    (block): block is HTMLElement => block instanceof HTMLElement && blockHasAuthoredContent(block),
  )
  if (sourceBlocks.length === 0) return null

  const checklist = type === 'checklist'
  const list = document.createElement(type === 'ordered' ? 'ol' : 'ul')
  if (checklist) list.setAttribute('data-checklist', 'true')

  let firstItem: HTMLLIElement | null = null
  for (const sourceBlock of sourceBlocks) {
    const item = document.createElement('li')
    while (sourceBlock.firstChild) item.append(sourceBlock.firstChild)
    if (!item.childNodes.length) {
      item.append(checklist ? emptyListItemEntry() : document.createElement('br'))
    }
    if (checklist) {
      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      item.append(checkbox)
    }
    list.append(item)
    firstItem ??= item
  }

  startBlock.before(list)
  for (const sourceBlock of selectedBlocks) sourceBlock.remove()
  return firstItem
}

/** Adds the disposable final editing line without putting it in Markdown. */
export function ensureTrailingEditableParagraph(root: HTMLElement): void {
  if (!blockHasAuthoredContent(root)) return
  let lastChild: Node | null = root.lastChild
  while (lastChild instanceof Text && !lastChild.data.trim()) lastChild = lastChild.previousSibling
  if (
    lastChild instanceof HTMLParagraphElement
    && !lastChild.textContent?.replace(/\u200B/g, '').trim()
  ) {
    if (!lastChild.childNodes.length) lastChild.append(document.createElement('br'))
    return
  }
  const paragraph = document.createElement('p')
  paragraph.append(document.createElement('br'))
  root.append(paragraph)
}

export function placeCaretInListItem(item: HTMLLIElement): void {
  const selection = window.getSelection()
  if (!selection) return
  const range = document.createRange()
  ensureChecklistItemTextEntry(item)
  const text = firstMeaningfulText(item)
  if (text) {
    range.setStart(text, text.length)
  } else {
    ensureChecklistItemTextEntry(item)
    const entry = item.querySelector(':scope > br')
    const entryIndex = entry ? [...item.childNodes].indexOf(entry) : 0
    range.setStart(item, entryIndex)
  }
  range.collapse(true)
  selection.removeAllRanges()
  selection.addRange(range)
}

export function placeCaretAtStartOfListItem(item: HTMLLIElement): void {
  const selection = window.getSelection()
  if (!selection) return
  const range = document.createRange()
  const text = firstListItemTextEntry(item)
  if (text) {
    range.setStart(text, 0)
  } else {
    const entry = item.querySelector(':scope > br')
    const entryIndex = entry ? [...item.childNodes].indexOf(entry) : 0
    range.setStart(item, entryIndex)
  }
  range.collapse(true)
  selection.removeAllRanges()
  selection.addRange(range)
}

function firstListItemTextEntry(item: HTMLLIElement): Text | null {
  ensureChecklistItemTextEntry(item)
  return firstMeaningfulText(item)
}

/**
 * Distinguishes Enter at the logical beginning of a row from Enter inside its
 * content. An empty checklist row starts at its leading editable `<br>`.
 */
export function isCaretAtStartOfListItem(item: HTMLLIElement, range: Range): boolean {
  if (!range.collapsed) return false
  const legacyAnchorStart = range.startContainer instanceof Text
    && range.startContainer.data.startsWith('\u200B')
    && range.startOffset === 1
  const text = firstListItemTextEntry(item)
  if (text) return legacyAnchorStart || (range.startContainer === text && range.startOffset === 0)
  const entry = item.querySelector(':scope > br')
  const entryIndex = entry ? [...item.childNodes].indexOf(entry) : 0
  return range.startContainer === item && range.startOffset === entryIndex
}

export function splitListItem(
  item: HTMLLIElement,
  sourceRange: Range,
  checklist: boolean,
): HTMLLIElement {
  const sourceCheckbox = checklist
    ? item.querySelector<HTMLInputElement>(':scope > input[type="checkbox"]')
    : null
  if (sourceCheckbox && sourceCheckbox !== item.lastChild) item.append(sourceCheckbox)

  const range = sourceRange.cloneRange()
  if (!range.collapsed) range.deleteContents()

  const editableChildCount = sourceCheckbox
    ? [...item.childNodes].indexOf(sourceCheckbox)
    : item.childNodes.length
  if (range.startContainer === item && range.startOffset > editableChildCount) {
    range.setStart(item, editableChildCount)
    range.collapse(true)
  }

  const tail = document.createRange()
  tail.setStart(range.startContainer, range.startOffset)
  tail.setEnd(item, editableChildCount)
  const remainder = tail.extractContents()

  const next = document.createElement('li')
  next.append(remainder)
  for (const child of [...next.childNodes]) {
    if (child instanceof Text && !child.data) child.remove()
  }
  for (const inline of next.querySelectorAll('strong, b, em, i, mark, a, span')) {
    if (!(inline.textContent ?? '') && !inline.querySelector('br, img, input')) inline.remove()
  }
  if (!next.textContent?.trim() && !next.querySelector('br')) {
    next.append(checklist ? emptyListItemEntry() : document.createElement('br'))
  }
  if (checklist) {
    const checkbox = document.createElement('input')
    checkbox.type = 'checkbox'
    next.append(checkbox)
  }
  item.after(next)
  return next
}

/** Logical authored lines, independent of browser wrapping or IME paragraph shape. */
function paragraphLineRanges(block: HTMLElement): Range[] {
  const breaks = [...block.querySelectorAll('br')]
  // A final BR is the browser's caret placeholder, not another authored line.
  const last = breaks.at(-1)
  if (last) {
    const tail = document.createRange()
    tail.selectNodeContents(block)
    tail.setStartAfter(last)
    const content = tail.cloneContents()
    if (!content.textContent && !content.querySelector('img, input, br')) breaks.pop()
  }
  const lines: Range[] = []
  let previous: HTMLBRElement | undefined
  for (const boundary of [...breaks, undefined]) {
    const line = document.createRange()
    line.selectNodeContents(block)
    if (previous) line.setStartAfter(previous)
    if (boundary) line.setEndBefore(boundary)
    lines.push(line)
    previous = boundary
  }
  return lines
}

function cloneParagraphLine(block: HTMLElement, line: Range): DocumentFragment {
  const content = line.cloneContents()
  let ancestor = line.commonAncestorContainer instanceof Element
    ? line.commonAncestorContainer : line.commonAncestorContainer.parentElement
  // cloneContents omits shared inline ancestors (e.g. one STRONG spanning BRs).
  while (ancestor && ancestor !== block) {
    const wrapper = ancestor.cloneNode(false) as Element
    wrapper.removeAttribute('id')
    wrapper.append(...content.childNodes)
    content.append(wrapper)
    ancestor = ancestor.parentElement
  }
  return content
}

function insertParagraphLinesAsList(block: HTMLElement, selection: Range, type: ListType): HTMLLIElement {
  const lines = paragraphLineRanges(block)
  const lineAt = (node: Node, offset: number) => {
    const found = lines.findIndex(line => line.comparePoint(node, offset) <= 0)
    return found < 0 ? lines.length - 1 : found
  }
  const first = lineAt(selection.startContainer, selection.startOffset)
  let last = lineAt(selection.endContainer, selection.endOffset)
  if (!selection.collapsed && last > first) {
    const prefix = lines[last]!.cloneRange()
    prefix.setEnd(selection.endContainer, selection.endOffset)
    const content = prefix.cloneContents()
    // A selection ending at the beginning of a following line excludes it,
    // including equivalent element-boundary and inline-text caret positions.
    if (!content.textContent && !content.querySelector('img, input, br')) last--
  }
  const list = document.createElement(type === 'ordered' ? 'ol' : 'ul')
  if (type === 'checklist') list.setAttribute('data-checklist', 'true')
  for (const line of lines.slice(first, last + 1)) {
    const item = document.createElement('li')
    const content = cloneParagraphLine(block, line)
    if (content.textContent || content.querySelector('img, input')) item.append(content)
    else if (!selection.collapsed && first !== last) continue
    else item.append(type === 'checklist' ? emptyListItemEntry() : document.createElement('br'))
    if (type === 'checklist') {
      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      item.append(checkbox)
    }
    list.append(item)
  }
  // Even a selection consisting entirely of empty lines needs an editable row.
  if (!list.firstChild) {
    const item = document.createElement('li')
    item.append(type === 'checklist' ? emptyListItemEntry() : document.createElement('br'))
    if (type === 'checklist') {
      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      item.append(checkbox)
    }
    list.append(item)
  }
  const retainedBlock = (range: Range) => {
    const retained = block.cloneNode(false) as HTMLElement
    retained.removeAttribute('id')
    retained.append(range.cloneContents())
    return retained
  }
  if (first > 0) {
    const before = document.createRange()
    before.selectNodeContents(block)
    before.setEnd(lines[first - 1]!.endContainer, lines[first - 1]!.endOffset)
    block.before(retainedBlock(before))
  }
  block.before(list)
  if (last < lines.length - 1) {
    const after = document.createRange()
    after.selectNodeContents(block)
    after.setStart(lines[last + 1]!.startContainer, lines[last + 1]!.startOffset)
    block.before(retainedBlock(after))
  }
  block.remove()
  return list.firstElementChild as HTMLLIElement
}

export function insertList(
  root: HTMLElement,
  currentBlock: HTMLElement | null,
  sourceRange: Range,
  type: ListType,
): HTMLLIElement {
  if (currentBlock && currentBlock !== root && currentBlock.matches('p, div')
    && !currentBlock.querySelector('p, div, ul, ol, blockquote, pre')
    && currentBlock.contains(sourceRange.startContainer) && currentBlock.contains(sourceRange.endContainer)) {
    return insertParagraphLinesAsList(currentBlock, sourceRange, type)
  }
  const checklist = type === 'checklist'
  const list = document.createElement(type === 'ordered' ? 'ol' : 'ul')
  const item = document.createElement('li')
  if (checklist) {
    list.setAttribute('data-checklist', 'true')
  }

  const block = currentBlock === root ? null : currentBlock
  if (block) {
    const onlyBreak = block.childNodes.length === 1 && block.firstChild?.nodeName === 'BR'
    if (!onlyBreak) {
      while (block.firstChild) item.append(block.firstChild)
    }
    block.replaceWith(list)
  } else {
    // Chromium can leave a newly typed line above a list as root-level text
    // (or inline spans), without a P/DIV. Convert that line, not an empty row
    // at the end of the editor. BR and block siblings delimit visual lines.
    let anchor: Node | null = sourceRange.startContainer
    while (anchor && anchor !== root && anchor.parentNode !== root) anchor = anchor.parentNode
    if (anchor === root) anchor = root.childNodes[sourceRange.startOffset] ?? null
    const isInline = (node: Node | null): node is ChildNode => Boolean(node
      && (node instanceof Text || (node instanceof HTMLElement
        && !node.matches('br, p, div, h1, h2, h3, h4, h5, h6, ul, ol, li, blockquote, pre, hr'))))
    if (isInline(anchor)) {
      while (isInline(anchor.previousSibling)) anchor = anchor.previousSibling
      root.insertBefore(list, anchor)
      while (isInline(anchor)) {
        const next: ChildNode | null = anchor.nextSibling
        item.append(anchor)
        anchor = next
      }
      if (anchor instanceof HTMLBRElement) anchor.remove()
    } else {
      root.insertBefore(list, anchor)
    }
  }

  if (!item.childNodes.length) item.append(checklist ? emptyListItemEntry() : document.createElement('br'))
  if (checklist) {
    const checkbox = document.createElement('input')
    checkbox.type = 'checkbox'
    item.append(checkbox)
  }
  list.append(item)
  return item
}

export function insertUnorderedList(
  root: HTMLElement,
  currentBlock: HTMLElement | null,
  sourceRange: Range,
  checklist: boolean,
): HTMLLIElement {
  return insertList(root, currentBlock, sourceRange, checklist ? 'checklist' : 'bullet')
}

function cloneListShell(list: HTMLElement, orderedStart?: number): HTMLElement {
  const clone = list.cloneNode(false) as HTMLElement
  if (list.tagName === 'OL' && orderedStart !== undefined) {
    clone.setAttribute('start', String(orderedStart))
  }
  return clone
}

export function extractListItem(item: HTMLLIElement): HTMLParagraphElement {
  const list = item.parentElement
  if (!list || !getListType(list)) throw new Error('List item must be inside a supported list')
  const items = [...list.children].filter((child): child is HTMLLIElement => child.tagName === 'LI')
  const index = items.indexOf(item)
  const orderedStart = list.tagName === 'OL'
    ? Number.parseInt(list.getAttribute('start') ?? '1', 10) || 1
    : undefined
  const before = cloneListShell(list, orderedStart)
  const after = cloneListShell(
    list,
    orderedStart === undefined ? undefined : orderedStart + index + 1,
  )
  for (const sibling of items.slice(0, index)) before.append(sibling)
  for (const sibling of items.slice(index + 1)) after.append(sibling)

  const paragraph = document.createElement('p')
  for (const child of [...item.childNodes]) {
    if (child instanceof HTMLInputElement && child.type === 'checkbox') {
      child.remove()
      continue
    }
    if (child instanceof HTMLParagraphElement) {
      while (child.firstChild) paragraph.append(child.firstChild)
      child.remove()
      continue
    }
    paragraph.append(child)
  }
  if (!paragraph.childNodes.length) paragraph.append(document.createElement('br'))

  const replacement: Node[] = []
  if (before.children.length) replacement.push(before)
  replacement.push(paragraph)
  if (after.children.length) replacement.push(after)
  list.replaceWith(...replacement)
  return paragraph
}

export function moveListItem(item: HTMLLIElement, direction: -1 | 1): boolean {
  const sibling = direction < 0 ? item.previousElementSibling : item.nextElementSibling
  if (!(sibling instanceof HTMLLIElement)) return false
  if (direction < 0) sibling.before(item)
  else sibling.after(item)
  return true
}

function matchingNestedList(item: HTMLLIElement, type: ListType): HTMLElement | null {
  return [...item.children].find((child): child is HTMLElement =>
    (child.tagName === 'UL' || child.tagName === 'OL') && getListType(child) === type,
  ) ?? null
}

function createNestedList(list: HTMLElement): HTMLElement {
  const nested = list.cloneNode(false) as HTMLElement
  // Nested ordered lists restart at one, while their semantic list type and
  // checklist controls remain identical to the parent list.
  if (nested.tagName === 'OL') nested.removeAttribute('start')
  return nested
}

function appendBeforeChecklistControl(item: HTMLLIElement, child: HTMLElement): void {
  const checkbox = item.querySelector<HTMLInputElement>(':scope > input[type="checkbox"]')
  item.insertBefore(child, checkbox ?? null)
}

export function indentListItem(item: HTMLLIElement): boolean {
  const list = item.parentElement
  const type = list ? getListType(list) : null
  const previousItem = item.previousElementSibling
  if (!list || !type || !(previousItem instanceof HTMLLIElement)) return false

  const nested = matchingNestedList(previousItem, type) ?? createNestedList(list)
  if (!nested.parentElement) appendBeforeChecklistControl(previousItem, nested)
  nested.append(item)
  return true
}

export function outdentListItem(item: HTMLLIElement): boolean {
  const nestedList = item.parentElement
  const type = nestedList ? getListType(nestedList) : null
  const parentItem = nestedList?.parentElement
  const outerList = parentItem?.parentElement
  if (
    !nestedList
    || !type
    || !(parentItem instanceof HTMLLIElement)
    || !outerList
    || getListType(outerList) !== type
  ) return false

  const followingItems = [...nestedList.children]
    .slice([...nestedList.children].indexOf(item) + 1)
    .filter((child): child is HTMLLIElement => child instanceof HTMLLIElement)
  if (followingItems.length) {
    const continuation = createNestedList(nestedList)
    for (const followingItem of followingItems) continuation.append(followingItem)
    appendBeforeChecklistControl(item, continuation)
  }
  parentItem.after(item)
  if (!nestedList.children.length) nestedList.remove()
  return true
}

export function indentTextAtRange(sourceRange: Range): void {
  const indent = document.createTextNode(EDITOR_TEXT_INDENT)
  const range = sourceRange.cloneRange()
  range.collapse(true)
  range.insertNode(indent)
  range.setStart(indent, indent.length)
  range.collapse(true)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
}

export function outdentTextAtRange(sourceRange: Range): boolean {
  if (!sourceRange.collapsed) return false
  let text: Text | null = sourceRange.startContainer instanceof Text
    ? sourceRange.startContainer
    : null
  let offset = text ? sourceRange.startOffset : 0
  if (!text && sourceRange.startContainer instanceof HTMLElement) {
    let previous: Node | null = sourceRange.startContainer.childNodes[sourceRange.startOffset - 1] ?? null
    while (previous?.lastChild) previous = previous.lastChild
    if (previous instanceof Text) {
      text = previous
      offset = previous.length
    }
  }
  if (!text || !text.data.slice(0, offset).endsWith(EDITOR_TEXT_INDENT)) {
    const anchor = sourceRange.startContainer instanceof HTMLElement
      ? sourceRange.startContainer
      : sourceRange.startContainer.parentElement
    const block = anchor?.closest('p, div, h1, h2, h3, h4, h5, h6, blockquote')
    const walker = block && document.createTreeWalker(block, NodeFilter.SHOW_TEXT)
    const firstText = walker?.nextNode() as Text | null
    if (!firstText?.data.startsWith(EDITOR_TEXT_INDENT)) return false
    text = firstText
    offset = EDITOR_TEXT_INDENT.length
  }

  text.deleteData(offset - EDITOR_TEXT_INDENT.length, EDITOR_TEXT_INDENT.length)
  const range = document.createRange()
  range.setStart(text, offset - EDITOR_TEXT_INDENT.length)
  range.collapse(true)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
  return true
}

export function reorderChecklistItem(item: HTMLLIElement, checked: boolean): boolean {
  const list = item.parentElement
  if (!list?.hasAttribute('data-checklist')) return false

  const originalIndex = [...list.children].indexOf(item)
  if (checked) {
    list.append(item)
  } else {
    const firstCheckedItem = [...list.children].find((candidate) =>
      candidate !== item
      && candidate instanceof HTMLLIElement
      && Boolean(candidate.querySelector(':scope > input[type="checkbox"]:checked')),
    )
    if (firstCheckedItem) firstCheckedItem.before(item)
    else list.append(item)
  }
  return [...list.children].indexOf(item) !== originalIndex
}

export function ensureEditableParagraphAfterList(list: HTMLElement): HTMLParagraphElement {
  const nextBlock = list.nextElementSibling
  if (nextBlock instanceof HTMLParagraphElement && !nextBlock.textContent?.trim()) {
    if (!nextBlock.childNodes.length) nextBlock.append(document.createElement('br'))
    return nextBlock
  }

  const paragraph = document.createElement('p')
  paragraph.append(document.createElement('br'))
  list.after(paragraph)
  return paragraph
}

export function deleteListItem(item: HTMLLIElement): HTMLElement | null {
  const list = item.parentElement
  if (!list || !getListType(list)) return null

  const nextItem = item.nextElementSibling instanceof HTMLLIElement
    ? item.nextElementSibling
    : item.previousElementSibling instanceof HTMLLIElement
      ? item.previousElementSibling
      : null
  if (nextItem) {
    item.remove()
    return nextItem
  }

  const paragraph = document.createElement('p')
  paragraph.append(document.createElement('br'))
  list.replaceWith(paragraph)
  return paragraph
}

export function exitQuote(quote: HTMLElement): HTMLParagraphElement {
  const paragraph = document.createElement('p')
  paragraph.append(document.createElement('br'))
  quote.after(paragraph)
  return paragraph
}

export function insertDivider(
  root: HTMLElement,
  currentBlock: HTMLElement | null,
): HTMLParagraphElement {
  const divider = document.createElement('hr')
  const paragraph = document.createElement('p')
  paragraph.append(document.createElement('br'))
  const block = currentBlock?.tagName === 'LI' ? currentBlock.parentElement : currentBlock

  if (!block || block === root) {
    root.append(divider, paragraph)
  } else if (!block.textContent?.trim() && ['P', 'DIV'].includes(block.tagName)) {
    block.replaceWith(divider, paragraph)
  } else {
    block.after(divider, paragraph)
  }
  return paragraph
}
